/**
 * The accessibility modes: the backend's five (`session.accessibility.mode`,
 * backend/backend/harness/state/accessibility.py), and what each one changes on screen.
 *
 * A mode is data in four parts:
 *  - layout       changes applied after the preset, domain and screen (it wins over them)
 *  - constraints  what must hold whatever the preset did (motor: what you touch stays reachable)
 *  - look         attributes on #kiosk-root that tokens.css turns into sizes, contrast, motion
 *  - behaviour    what the modules do differently (microphone, swipe, captions)
 *
 * The backend side of each mode (verbose TTS, no TTS, longer timeouts, simple language) stays in the backend.
 */
import type { LayoutChange } from '../layout/changes';
import type { LayoutConstraint } from '../layout/resolve';
import type { AccessibilityMode } from '../core/wire';

export interface ModeBehaviour {
  /** The microphone: hearing mode turns the voice off, as the backend does (needs_voice_disabled). */
  microphone: 'allowed' | 'off';
  /** Swiping carousels: off where a precise drag is hard (motor). */
  swipe: boolean;
  /** Layout transitions: off where movement distracts (cognitive). */
  motion: 'full' | 'reduce';
  /** The chat text size. */
  captions: 'normal' | 'large';
}

export interface ModeDefinition {
  id: AccessibilityMode;
  label: string;
  icon: string;
  layout: LayoutChange[];
  constraints: LayoutConstraint[];
  look: Record<string, string>;
  behaviour: ModeBehaviour;
}

/** Half of the 1920 canvas: below it a seated person reaches comfortably. */
export const REACH_LINE = 960;

const standard: ModeBehaviour = { microphone: 'allowed', swipe: true, motion: 'full', captions: 'normal' };

export const MODES: Record<AccessibilityMode, ModeDefinition> = {
  none: {
    id: 'none', label: 'mode.none', icon: 'user', layout: [], constraints: [], look: {}, behaviour: standard,
  },
  visual: {
    id: 'visual', label: 'mode.visual', icon: 'eye',
    // Big, high-contrast text needs the width the avatar takes: the text layout, the box under the chat (the
    // avatar's column, left empty, goes), on the theme's own plain background (dark on a dark theme).
    layout: [
      { op: 'hide', module: 'avatar-window' },
      { op: 'move', module: 'composer', into: 'conversation' },
      { op: 'scene', scene: 'plain' },
    ],
    constraints: [],
    look: { contrast: 'high', 'text-size': 'larger', targets: 'large' },
    behaviour: { ...standard, captions: 'large' },
  },
  hearing: {
    id: 'hearing', label: 'mode.hearing', icon: 'ear',
    layout: [], constraints: [],
    look: { 'text-size': 'large' },
    behaviour: { ...standard, microphone: 'off', captions: 'large' },
  },
  motor: {
    id: 'motor', label: 'mode.motor', icon: 'accessibility',
    // The keyboard row joins the main bar, and the content gets shorter so its top is within reach.
    layout: [
      { op: 'move', module: 'composer', into: 'root', before: 'main-bar' },
      { op: 'resize', target: 'content', size: { px: 600 } },
      { op: 'resize', target: 'conversation', size: { fr: 1 } },
      // the text box no longer covers the avatar's lower edge: the stage goes down until the task's card hides it
      { op: 'stageOffset', y: 48 },
    ],
    constraints: [{ kind: 'reachable', source: 'mode:motor', modules: ['content', 'main-bar', 'composer'], minTop: REACH_LINE }],
    look: { targets: 'large' },
    behaviour: { ...standard, swipe: false },
  },
  cognitive: {
    id: 'cognitive', label: 'mode.cognitive', icon: 'brain',
    layout: [],
    constraints: [],
    look: { motion: 'reduce', 'text-size': 'large', targets: 'large' },
    behaviour: { ...standard, motion: 'reduce', captions: 'large' },
  },
};
